/**
 * GLAMRDiP colours grid interactions
 * - Desktop ≥990: filter checkboxes + sort dropdown auto-submit on change
 *   (debounced 200ms so multi-clicks are coalesced).
 * - Mobile <990: filter drawer slides in from left, body scroll-locked,
 *   backdrop click closes, Esc closes. Form submits via the explicit
 *   "Apply" button (no auto-submit on mobile so users can multi-select
 *   before reload).
 *
 * Submit strategy: build URLSearchParams from checked inputs + price
 * range + sort. Set window.location.search directly so URL params persist
 * (Shopify's storefront filters work entirely off URL params — no AJAX).
 */
(() => {
  'use strict';
  const form     = document.querySelector('[data-gd-colours-filters]');
  const drawer   = document.querySelector('[data-gd-filters-drawer]');
  const backdrop = document.querySelector('[data-gd-filters-backdrop]');
  // Joel 2026-05-07: querySelectorAll (was querySelector) — the snippet
  // renders TWO triggers when use_top_filter_bar is enabled:
  //   - .gd-colours__mobile-trigger (mobile-only, display: none ≥990px)
  //   - .gd-colours__top-filter-btn (full-width desktop top bar)
  // The previous singular query bound the click handler ONLY to the
  // first DOM match (mobile-trigger), so on desktop full-width mode
  // the visible top button had no listener → click did nothing →
  // filter drawer never opened.
  const openBtns = document.querySelectorAll('[data-gd-filters-open]');
  const openBtn  = openBtns[0] || null;   // first reference for focus restoration on close
  const closeBtns = document.querySelectorAll('[data-gd-filters-close]');
  const sort     = document.querySelector('[data-gd-sort]');
  if (!form && !sort && !drawer) return;

  const MOBILE_MQ = window.matchMedia ? window.matchMedia('(max-width: 989px)') : { matches: false };

  // Joel 2026-05-02: top-filter-bar mode (colours-grid section setting
  // `use_top_filter_bar`). When the root has `gd-colours--top-filter`,
  // the drawer is the ONLY way to apply filters even on desktop (no
  // sidebar) so we MUST disable the desktop auto-submit — otherwise
  // ticking the first checkbox immediately submits + reloads, killing
  // the multi-select UX inside the drawer. Apply button submits.
  const TOP_FILTER_MODE = !!document.querySelector('.gd-colours--top-filter');

  const buildSubmitUrl = () => {
    const params = new URLSearchParams();
    if (form) {
      form.querySelectorAll('input[type="checkbox"]:checked').forEach((cb) => {
        params.append(cb.name, cb.value);
      });
      // Price-range number inputs aren't checkboxes — append non-empty values.
      form.querySelectorAll('input[type="number"]').forEach((inp) => {
        if (inp.name && inp.value !== '') params.set(inp.name, inp.value);
      });
    }
    // Sort: on mobile, prefer the in-form radio (drawer drives sort
    // there). On desktop, prefer the toolbar select (drawer's sort
    // <details> is CSS-hidden so the radio holds the original
    // server-rendered value, not the user's intent). Joel 2026-05-01
    // #153: unified filter+sort drawer adds form radios for sort_by.
    const formSortRadio = form && form.querySelector('input[type="radio"][name="sort_by"]:checked');
    if (MOBILE_MQ.matches && formSortRadio) {
      params.set('sort_by', formSortRadio.value);
    } else if (sort && sort.value) {
      params.set('sort_by', sort.value);
    } else if (formSortRadio) {
      params.set('sort_by', formSortRadio.value);
    }
    const url = new URL(window.location.href);
    url.search = params.toString();
    return url.toString();
  };

  const submit = () => { window.location.href = buildSubmitUrl(); };

  // Desktop auto-submit (debounced). On mobile rely on the Apply button.
  // In top-filter-bar mode, also rely on the Apply button on desktop —
  // the drawer is the only filter UI and auto-submit would close it
  // mid-multi-select.
  let debounceTimer = null;
  const submitDebounced = () => {
    if (MOBILE_MQ.matches || TOP_FILTER_MODE) return;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(submit, 200);
  };
  form?.addEventListener('change', submitDebounced);
  sort?.addEventListener('change', submit);

  // Mobile Apply button — explicit submit. Intercept the form's native
  // submit so we use the same buildSubmitUrl path.
  form?.addEventListener('submit', (e) => {
    e.preventDefault();
    submit();
  });

  // ────── Drawer (mobile) ──────
  const openDrawer = () => {
    if (!drawer) return;
    drawer.classList.add('is-open');
    backdrop?.classList.add('is-open');
    // Joel 2026-04-30: shared iOS-safe scroll-lock instead of naive
    // body.overflow:hidden. Stops scroll-chain from the filter drawer
    // body to the page underneath on iOS Safari.
    if (window.gdScrollLock) window.gdScrollLock.lock(drawer);
    else document.body.style.overflow = 'hidden';
    const firstFocusable = drawer.querySelector('button, a, input:not([type=hidden])');
    firstFocusable?.focus();
  };
  const closeDrawer = () => {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    backdrop?.classList.remove('is-open');
    if (window.gdScrollLock) window.gdScrollLock.unlock(drawer);
    else document.body.style.overflow = '';
    openBtn?.focus();
  };

  openBtns.forEach((b) => b.addEventListener('click', openDrawer));
  closeBtns.forEach((b) => b.addEventListener('click', closeDrawer));
  backdrop?.addEventListener('click', closeDrawer);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer?.classList.contains('is-open')) closeDrawer();
  });

  // ────── Filter state restoration (Joel 2026-05-07) ──────
  // Server-side Liquid already renders the form's checkboxes with
  // `checked` based on URL params (gd-product-grid.liquid line ~149:
  // `{% if value.active %}checked{% endif %}`). So a fresh fetch with
  // a filter URL produces the correct HTML — verified via curl.
  //
  // This restoration handler is defensive belt-and-braces for two
  // edge cases:
  //   1. bfcache restore (browser back from PDP) — `pageshow` event
  //      fires with `e.persisted === true`. The page is restored
  //      from memory exactly as it was at unload, so checkboxes
  //      should already be in the correct state. But if any other
  //      script has mutated state since (e.g. an app embed), this
  //      re-syncs to URL truth.
  //   2. Hydration mismatch — if any client-side script clears
  //      checkbox state during DOMContentLoaded (race condition with
  //      app embeds), this re-applies the URL truth after.
  //
  // No auto-submit: the function ONLY toggles checkbox state; no
  // form submission, no URL change, no grid re-render. Server-
  // rendered grid is already correct for the URL.
  function restoreFilterStateFromURL() {
    if (!form) return;
    const params = new URLSearchParams(window.location.search);
    let changed = false;
    for (const [key, value] of params.entries()) {
      if (!key.startsWith('filter.')) continue;
      // CSS.escape guards against filter param names that may contain
      // dots / special chars (e.g. `filter.v.option.colour_family`).
      try {
        const cb = form.querySelector(
          `input[type="checkbox"][name="${CSS.escape(key)}"][value="${CSS.escape(value)}"]`
        );
        if (cb && !cb.checked) {
          cb.checked = true;
          changed = true;
        }
      } catch {}
    }
    // Sort radio: same restore for the form's radio + the toolbar
    // <select> (whichever is present in the current layout).
    const sortValue = params.get('sort_by');
    if (sortValue) {
      const radio = form.querySelector(
        `input[type="radio"][name="sort_by"][value="${CSS.escape(sortValue)}"]`
      );
      if (radio && !radio.checked) { radio.checked = true; changed = true; }
      if (sort && sort.value !== sortValue) { sort.value = sortValue; changed = true; }
    }
    return changed;
  }

  // Run once on DOMContentLoaded (covers initial load + standard
  // navigation). Also run on `pageshow` with persisted=true (bfcache
  // restore — covers the browser-back-from-PDP scenario).
  restoreFilterStateFromURL();
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) restoreFilterStateFromURL();
  });
})();
